import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import FloatingButtons from '../components/FloatingButtons/FloatingButtons';
import ProductCard from '../components/Products/ProductCard';
import { newArrivals, featuredCollection, specialOne, highlights } from '../data/products';
import { preWeddingProducts, kidsBirthdayProducts } from '../data/categoryProducts';
import { isPincodeServiceable, getCityForPincode } from '../data/pincodes';
import { SITE_NAME } from '../data/siteInfo';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [pincode, setPincode] = useState('');
  const [pincodeChecked, setPincodeChecked] = useState(false);
  const [isServiceable, setIsServiceable] = useState(false);
  const [pincodeMessage, setPincodeMessage] = useState('');
  const [openSection, setOpenSection] = useState(null);
  const [isBookingFormOpen, setIsBookingFormOpen] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    address: '',
    bookingDate: '',
    contactNumber: '',
    whatsappNumber: '',
    email: '',
    additionalNotes: ''
  });

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Handle body scroll when booking form is open
  useEffect(() => {
    if (isBookingFormOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isBookingFormOpen]);

  // Get all products
  const allProducts = [
    ...newArrivals,
    ...featuredCollection,
    ...specialOne,
    ...highlights.birthday,
    ...highlights.anniversary,
    ...highlights.babyShower,
    ...highlights.gifting,
    ...preWeddingProducts,
    ...kidsBirthdayProducts
  ];

  // Find current product
  const product = allProducts.find((p) => p.id === parseInt(id));

  // Get related products (exclude current product)
  const relatedProducts = allProducts
    .filter((p) => p.id !== parseInt(id))
    .slice(0, 5);

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center">
          <h1 className="text-3xl font-bold text-[#8B1538] mb-4">Product Not Found</h1>
          <button
            onClick={() => navigate('/')}
            className="bg-[#8B1538] text-white px-6 py-2 rounded-lg hover:bg-[#6B0F2A] transition-colors"
          >
            Go to Home
          </button>
        </main>
        <Footer />
        <FloatingButtons />
      </div>
    );
  }

  const handleQuantityChange = (delta) => {
    setQuantity((prev) => Math.max(1, prev + delta));
  };

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const handleCheckPincode = () => {
    if (pincode.length !== 6) {
      setPincodeMessage('Please enter a valid 6-digit pincode');
      setIsServiceable(false);
      setPincodeChecked(true);
      return;
    }

    const serviceable = isPincodeServiceable(pincode);
    setIsServiceable(serviceable);
    setPincodeChecked(true);

    if (serviceable) {
      const city = getCityForPincode(pincode);
      setPincodeMessage(`✓ Service available in ${city}! You can proceed with booking.`);
    } else {
      setPincodeMessage('✗ Service not available in this area. Please contact us for more information.');
    }
  };

  const handleBookNow = () => {
    if (!isServiceable) {
      alert('Please check service availability first by entering a valid pincode.');
      return;
    }
    // Open booking form slider
    setIsBookingFormOpen(true);
  };

  const handleCloseBookingForm = () => {
    setIsBookingFormOpen(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setBookingForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitBooking = (e) => {
    e.preventDefault();
    // Handle form submission
    const bookingData = {
      product: {
        title: product.title,
        price: product.price,
        quantity: quantity,
        total: product.price * quantity
      },
      pincode: pincode,
      ...bookingForm
    };
    
    console.log('Booking Data:', bookingData);
    alert('Booking submitted successfully! We will contact you soon.');
    
    // Reset form and close
    setBookingForm({
      name: '',
      address: '',
      bookingDate: '',
      contactNumber: '',
      whatsappNumber: '',
      email: '',
      additionalNotes: ''
    });
    handleCloseBookingForm();
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: product.title,
        text: `Check out ${product.title} on ${SITE_NAME}`,
        url: window.location.href
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 mb-12">
          {/* Left Side - Product Image */}
          <div className="w-full">
            <div className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden">
              <img
                src={product.image}
                alt={product.title}
                loading="eager"
                decoding="async"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 right-4 text-xs font-semibold text-gray-600 opacity-50 bg-white px-2 py-1 rounded">
                {SITE_NAME}
              </div>
            </div>
          </div>

          {/* Right Side - Product Details */}
          <div className="flex flex-col">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {product.title}
            </h1>

            <div className="mb-6">
              <span className="text-3xl sm:text-4xl font-bold text-gray-900">
                Rs. {product.price.toLocaleString('en-IN')}
              </span>
              {product.originalPrice && (
                <span className="text-lg sm:text-xl text-gray-500 line-through ml-3">
                  Rs. {product.originalPrice.toLocaleString('en-IN')}
                </span>
              )}
            </div>

            <p className="text-sm text-gray-600 mb-6">
              Tax included.{' '}
              <a href="#" className="underline hover:text-[#8B1538]">
                Shipping calculated at checkout
              </a>
            </p>

            {/* Quantity Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity:
              </label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleQuantityChange(-1)}
                  className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  value={quantity}
                  readOnly
                  className="w-16 h-10 text-center border border-gray-300 rounded-lg"
                />
                <button
                  onClick={() => handleQuantityChange(1)}
                  className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            {/* Service Availability Check */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <svg
                  className="w-5 h-5 text-[#8B1538]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="font-medium text-gray-900">Check Service Availability</span>
              </div>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Enter 6-digit pincode"
                  value={pincode}
                  onChange={(e) => {
                    const newPincode = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setPincode(newPincode);
                    // Reset check status when pincode changes
                    if (pincodeChecked) {
                      setPincodeChecked(false);
                      setIsServiceable(false);
                      setPincodeMessage('');
                    }
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B1538] focus:border-transparent"
                  maxLength={6}
                />
                <button 
                  onClick={handleCheckPincode}
                  className="bg-[#8B1538] hover:bg-[#6B0F2A] text-white px-6 py-2 rounded-lg transition-colors font-medium"
                >
                  Check Now
                </button>
              </div>
              {pincodeChecked && (
                <p className={`text-xs mb-2 ${isServiceable ? 'text-green-600' : 'text-red-600'}`}>
                  {pincodeMessage}
                </p>
              )}
              {!pincodeChecked && (
                <p className="text-xs text-gray-500">
                  Enter your pin code to check if we deliver to you.
                </p>
              )}
            </div>

            {/* Book Now Button */}
            <button
              onClick={handleBookNow}
              disabled={!isServiceable}
              className={`w-full font-semibold py-4 px-6 rounded-lg text-lg transition-colors duration-300 mb-4 ${
                isServiceable
                  ? 'bg-[#8B1538] hover:bg-[#6B0F2A] text-white cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Book now
            </button>

            {/* Share */}
            <button
              onClick={handleShare}
              className="flex items-center gap-2 text-[#8B1538] hover:text-[#6B0F2A] transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                />
              </svg>
              <span>Share</span>
            </button>
          </div>
        </div>

        {/* Expandable Sections */}
        <div className="border-t border-gray-200 pt-8 mb-12">
          <div className="space-y-0">
            {/* Description */}
            <div className="border-b border-gray-200">
              <button
                onClick={() => toggleSection('description')}
                className="w-full flex items-center justify-between py-4 text-left"
              >
                <span className="text-lg font-semibold text-gray-900">Description</span>
                <svg
                  className={`w-6 h-6 text-[#8B1538] transition-transform ${
                    openSection === 'description' ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {openSection === 'description' && (
                <div className="pb-4 text-gray-600 leading-relaxed">
                  <p>
                    Transform your celebration with our premium decoration services. Our expert team
                    brings creativity and attention to detail to every event, ensuring your special
                    moments are beautifully adorned. From elegant balloon arrangements to stunning
                    backdrops, we create magical atmospheres that make your celebrations
                    unforgettable.
                  </p>
                  <p className="mt-4">
                    All our decorations are professionally designed and installed, using
                    high-quality materials that are both beautiful and safe. We offer a wide range
                    of themes and styles to match your event's unique personality.
                  </p>
                </div>
              )}
            </div>

            {/* Need to Know – General Guidelines */}
            <div className="border-b border-gray-200">
              <button
                onClick={() => toggleSection('guidelines')}
                className="w-full flex items-center justify-between py-4 text-left"
              >
                <span className="text-lg font-semibold text-gray-900">
                  Need to Know – General Guidelines
                </span>
                <svg
                  className={`w-6 h-6 text-[#8B1538] transition-transform ${
                    openSection === 'guidelines' ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {openSection === 'guidelines' && (
                <div className="pb-4 text-gray-600 leading-relaxed">
                  <ul className="list-disc list-inside space-y-2">
                    <li>Please book at least 48 hours in advance for best availability</li>
                    <li>Our team will arrive 2-3 hours before your event for setup</li>
                    <li>All decorations are professionally installed and removed</li>
                    <li>We use eco-friendly and safe materials for all decorations</li>
                    <li>Customization options are available upon request</li>
                    <li>Setup time may vary based on decoration complexity</li>
                    <li>We provide full cleanup service after the event</li>
                  </ul>
                </div>
              )}
            </div>

            {/* Cancellation & Refund Policy */}
            <div className="border-b border-gray-200">
              <button
                onClick={() => toggleSection('policy')}
                className="w-full flex items-center justify-between py-4 text-left"
              >
                <span className="text-lg font-semibold text-gray-900">
                  Cancellation & Refund Policy
                </span>
                <svg
                  className={`w-6 h-6 text-[#8B1538] transition-transform ${
                    openSection === 'policy' ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {openSection === 'policy' && (
                <div className="pb-4 text-gray-600 leading-relaxed">
                  <p className="mb-4">
                    <strong>Cancellation Policy:</strong>
                  </p>
                  <ul className="list-disc list-inside space-y-2 mb-4">
                    <li>Cancellation 7+ days before event: Full refund</li>
                    <li>Cancellation 3-7 days before event: 50% refund</li>
                    <li>Cancellation less than 3 days before event: No refund</li>
                  </ul>
                  <p className="mb-4">
                    <strong>Refund Policy:</strong>
                  </p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>Refunds will be processed within 5-7 business days</li>
                    <li>Refunds will be credited to the original payment method</li>
                    <li>Service charges are non-refundable</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* You may also like */}
        {relatedProducts.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#8B1538] mb-6 sm:mb-8">
              You may also like
            </h2>
            <div className="flex overflow-x-auto gap-4 sm:gap-6 pb-4 scrollbar-hide">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <FloatingButtons />

      {/* Booking Form Slider */}
      {isBookingFormOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300"
            onClick={handleCloseBookingForm}
          />
          
          {/* Slider */}
          <div
            className={`fixed top-0 right-0 h-full w-full sm:w-[500px] lg:w-[600px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
              isBookingFormOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="p-6 sm:p-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#8B1538]">
                  Book Your Order
                </h2>
                <button
                  onClick={handleCloseBookingForm}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Close"
                >
                  <svg
                    className="w-6 h-6 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Pre-filled Product Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Product Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Product:</span>
                    <span className="font-medium text-gray-900">{product.title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Rate:</span>
                    <span className="font-medium text-gray-900">
                      Rs. {product.price.toLocaleString('en-IN')} per unit
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Quantity:</span>
                    <span className="font-medium text-gray-900">{quantity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Amount:</span>
                    <span className="font-bold text-[#8B1538] text-lg">
                      Rs. {(product.price * quantity).toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pincode:</span>
                    <span className="font-medium text-gray-900">{pincode}</span>
                  </div>
                </div>
              </div>

              {/* Booking Form */}
              <form onSubmit={handleSubmitBooking} className="space-y-4">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={bookingForm.name}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8B1538] focus:border-transparent outline-none transition"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Contact Number */}
                <div>
                  <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="contactNumber"
                    name="contactNumber"
                    required
                    value={bookingForm.contactNumber}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8B1538] focus:border-transparent outline-none transition"
                    placeholder="Enter your contact number"
                    maxLength={10}
                    pattern="[0-9]{10}"
                  />
                </div>

                {/* WhatsApp Number */}
                <div>
                  <label htmlFor="whatsappNumber" className="block text-sm font-medium text-gray-700 mb-2">
                    WhatsApp Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="whatsappNumber"
                    name="whatsappNumber"
                    required
                    value={bookingForm.whatsappNumber}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8B1538] focus:border-transparent outline-none transition"
                    placeholder="Enter your WhatsApp number"
                    maxLength={10}
                    pattern="[0-9]{10}"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={bookingForm.email}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8B1538] focus:border-transparent outline-none transition"
                    placeholder="Enter your email (optional)"
                  />
                </div>

                {/* Address */}
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                    Delivery Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    required
                    rows="3"
                    value={bookingForm.address}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8B1538] focus:border-transparent outline-none transition resize-none"
                    placeholder="Enter your complete delivery address"
                  />
                </div>

                {/* Booking Date */}
                <div>
                  <label htmlFor="bookingDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Booking Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="bookingDate"
                    name="bookingDate"
                    required
                    value={bookingForm.bookingDate}
                    onChange={handleFormChange}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8B1538] focus:border-transparent outline-none transition"
                  />
                </div>

                {/* Additional Notes */}
                <div>
                  <label htmlFor="additionalNotes" className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes / Special Instructions
                  </label>
                  <textarea
                    id="additionalNotes"
                    name="additionalNotes"
                    rows="3"
                    value={bookingForm.additionalNotes}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8B1538] focus:border-transparent outline-none transition resize-none"
                    placeholder="Any special instructions or requirements..."
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-4 space-y-3">
                  <button
                    type="submit"
                    className="w-full bg-[#8B1538] hover:bg-[#6B0F2A] text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
                  >
                    Confirm Booking
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseBookingForm}
                    className="w-full border-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductDetail;

